import { useState, useEffect, useMemo } from "react";
// Using custom modal overlay since Dialog component is not available
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { X, TrendingUp, Loader2 } from "lucide-react";
import ParameterStatusBadge from "@/components/parameter-status-badge.jsx";
import { formatParameterValue } from "@/lib/parameter-status-utils.js";
import { formatNumber } from "@/lib/formatting-utils.js";
import {
  getTagMeasurementsLast24Hours,
  getTagMeasurementsLast7Days,
  getTagMeasurementsLast30Days,
  getTagMeasurementsLast60Days,
} from "@/api/measurement-api";
import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts";
import { escapeTooltipHtml, formatTooltipValue, getChartTooltipChrome, formatChartTooltipDateTime } from "@/utils/chart-utils.js";
import { transformApiDataToChartFormat } from "@/lib/tag-data-utils.js";
import { arrayUtils, cn } from "@/lib/utils.js";
import { formatTimeAxis } from "@/lib/datetime-utils.js";
import {
  dateRangePillIdle,
  dateRangePillSelected,
  dateRangePillTrack,
} from "@/lib/status-styles.js";
import { useTheme } from "@/contexts/ThemeContext.jsx";

const ALARM_LINE_SEVERE = "#dc2626";
/** Warning setpoint legend swatch — matches chart warning bands */
const ALARM_LINE_WARNING_LIGHT = "#F1E86C";
const ALARM_LINE_WARNING_DARK = "#FFD633";

/**
 * Build y-axis alarm swimlane bands (no dashed setpoint lines) + legend rows.
 * When LoLo===Lo or Hi===HiHi (common), skip zero-width warning bands and
 * collapse coincident setpoints into one legend label.
 * @param {{ lolo?: unknown, lo?: unknown, hi?: unknown, hihi?: unknown }} setPoints
 * @param {number[]} seriesYs
 * @param {boolean} isDark
 */
function buildAlarmAxisChrome(setPoints, seriesYs, isDark) {
  const lolo = Number(setPoints?.lolo);
  const lo = Number(setPoints?.lo);
  const hi = Number(setPoints?.hi);
  const hihi = Number(setPoints?.hihi);

  let axisMin = seriesYs.length ? Math.min(...seriesYs) : NaN;
  let axisMax = seriesYs.length ? Math.max(...seriesYs) : NaN;
  for (const v of [lolo, lo, hi, hihi]) {
    if (!Number.isFinite(v)) continue;
    axisMin = Number.isFinite(axisMin) ? Math.min(axisMin, v) : v;
    axisMax = Number.isFinite(axisMax) ? Math.max(axisMax, v) : v;
  }
  if (!Number.isFinite(axisMin)) axisMin = 0;
  if (!Number.isFinite(axisMax)) axisMax = axisMin + 1;

  const span = Math.max(axisMax - axisMin, 1e-9);
  const pad = Math.max(span * 0.06, 1e-6);
  const yMin = axisMin - pad;
  const yMax = axisMax + pad;

  const alarmRed = isDark
    ? "rgba(239, 68, 68, 0.22)"
    : "rgba(254, 202, 202, 0.35)";
  /** LoLo–Lo and Hi–HiHi warning wash (rgba so bands stay translucent over the chart) */
  const alarmWarningBand = isDark
    ? "rgba(255, 214, 51, 0.3)"
    : "rgba(241, 232, 108, 0.45)";
  const alarmWarningLine = isDark
    ? ALARM_LINE_WARNING_DARK
    : ALARM_LINE_WARNING_LIGHT;

  const lowCollapsed =
    Number.isFinite(lolo) && Number.isFinite(lo) && lolo === lo;
  const highCollapsed =
    Number.isFinite(hi) && Number.isFinite(hihi) && hi === hihi;

  /** @type {Array<{ from: number, to: number, color: string, zIndex: number }>} */
  const plotBands = [];

  // Severe low: below LoLo (or below the shared LoLo/Lo threshold)
  if (Number.isFinite(lolo)) {
    plotBands.push({ from: yMin, to: lolo, color: alarmRed, zIndex: 0 });
  }

  // Warning low: LoLo → Lo (only when they differ)
  if (!lowCollapsed && Number.isFinite(lo) && Number.isFinite(lolo) && lo > lolo) {
    plotBands.push({
      from: lolo,
      to: lo,
      color: alarmWarningBand,
      zIndex: 0,
    });
  } else if (
    !lowCollapsed
    && Number.isFinite(lo)
    && !Number.isFinite(lolo)
  ) {
    // Lo alone: pale wash below Lo so the threshold still reads as a band
    plotBands.push({
      from: yMin,
      to: lo,
      color: alarmWarningBand,
      zIndex: 0,
    });
  }

  // Warning high: Hi → HiHi (only when they differ)
  if (
    !highCollapsed
    && Number.isFinite(hi)
    && Number.isFinite(hihi)
    && hihi > hi
  ) {
    plotBands.push({
      from: hi,
      to: hihi,
      color: alarmWarningBand,
      zIndex: 0,
    });
  } else if (
    !highCollapsed
    && Number.isFinite(hi)
    && !Number.isFinite(hihi)
  ) {
    plotBands.push({
      from: hi,
      to: yMax,
      color: alarmWarningBand,
      zIndex: 0,
    });
  }

  // Severe high: above HiHi (or above the shared Hi/HiHi threshold)
  if (Number.isFinite(hihi)) {
    plotBands.push({ from: hihi, to: yMax, color: alarmRed, zIndex: 0 });
  }

  /**
   * Deduplicate coincident setpoints; prefer severe (red) when LoLo/Lo or Hi/HiHi share a value.
   * @type {Map<number, { value: number, color: string, names: string[], severe: boolean }>}
   */
  const lineByValue = new Map();
  /**
   * @param {number} value
   * @param {string} name
   * @param {boolean} severe
   */
  const addLegendItem = (value, name, severe) => {
    if (!Number.isFinite(value)) return;
    const existing = lineByValue.get(value);
    if (!existing) {
      lineByValue.set(value, {
        value,
        color: severe ? ALARM_LINE_SEVERE : alarmWarningLine,
        names: [name],
        severe,
      });
      return;
    }
    if (!existing.names.includes(name)) existing.names.push(name);
    if (severe && !existing.severe) {
      existing.severe = true;
      existing.color = ALARM_LINE_SEVERE;
    }
  };

  addLegendItem(lolo, "LOLO", true);
  addLegendItem(lo, "LO", false);
  addLegendItem(hi, "HI", false);
  addLegendItem(hihi, "HIHI", true);

  /** Legend rows with collapsed coincident labels. */
  const legendItems = [...lineByValue.values()]
    .sort((a, b) => a.value - b.value)
    .map((line) => ({
      label: line.names.join(" / "),
      value: line.value,
      color: line.color,
    }));

  return { yMin, yMax, plotBands, legendItems };
}

/**
 * History Trend Popup Component
 * Shows trending data for a specific parameter with alarm swimlane bands.
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the popup is open
 * @param {Function} props.onClose - Function to close the popup
 * @param {Object} props.parameterData - Parameter data including name, unit, setpoints
 * @param {string} props.assetNumber - Asset number for display
 * @param {string} props.location - Asset location for display
 */
export default function HistoryTrendPopup({
  isOpen,
  onClose,
  parameterData,
  assetNumber,
  location,
}) {
  const { theme } = useTheme();
  const [selectedPeriod, setSelectedPeriod] = useState("24h");
  const [trendData, setTrendData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Time period options
  const timePeriods = [
    { value: "24h", label: "24 Hours" },
    { value: "7d", label: "7 Days" },
    { value: "30d", label: "30 Days" },
    { value: "60d", label: "60 Days" },
  ];

  /**
   * Fetch trending data from API for the selected time period
   */
  const fetchTrendData = async () => {
    if (
      !parameterData?.apiData?.assetTagId
      || !parameterData?.apiData?.assetId
    ) {
      setError("Missing asset information");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      let apiResponse;
      const { assetTagId, assetId } = parameterData.apiData;

      switch (selectedPeriod) {
        case "24h":
          apiResponse = await getTagMeasurementsLast24Hours(
            assetTagId,
            assetId
          );
          break;
        case "7d":
          apiResponse = await getTagMeasurementsLast7Days(assetTagId, assetId);
          break;
        case "30d":
          apiResponse = await getTagMeasurementsLast30Days(assetTagId, assetId);
          break;
        case "60d":
          apiResponse = await getTagMeasurementsLast60Days(assetTagId, assetId);
          break;
        default:
          throw new Error("Invalid time period selected");
      }

      // Transform API response to chart format
      const transformedData = transformApiDataToChartFormat(
        apiResponse,
        selectedPeriod,
        formatTimeAxis
      );
      setTrendData(transformedData);
    } catch (err) {
      console.error("Error fetching trend data:", err);
      setError("Failed to load trend data. Please try again.");
      setTrendData([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch data when period changes or component mounts
  useEffect(() => {
    if (isOpen && parameterData) {
      fetchTrendData();
    }
  }, [selectedPeriod, isOpen, parameterData]);

  // Prepare Highcharts configuration
  const chartOptions = useMemo(() => {
    const isDark = theme === "dark";
    const seriesYs = trendData
      .map((item) => Number(item.value))
      .filter((y) => Number.isFinite(y));
    const { yMin, yMax, plotBands } = buildAlarmAxisChrome(
      parameterData?.alarmSetPoints,
      seriesYs,
      isDark
    );

    return {
      chart: {
        type: "line",
        backgroundColor: "transparent",
      },
      title: {
        text: null,
      },
      xAxis: {
        type: "datetime",
        title: {
          text: null,
        },
        labels: {
          style: {
            fontSize: "12px",
            color: "hsl(var(--muted-foreground))",
          },
        },
        gridLineColor: "hsl(var(--border))",
      },
      yAxis: {
        title: {
          text: parameterData?.unit || "",
          rotation: -90,
          align: "high",
          style: {
            fontSize: "12px",
            color: "hsl(var(--muted-foreground))",
          },
        },
        labels: {
          style: {
            fontSize: "12px",
            color: "hsl(var(--muted-foreground))",
          },
        },
        gridLineColor: "hsl(var(--border))",
        min: yMin,
        max: yMax,
        plotBands: plotBands.length ? plotBands : undefined,
      },
      tooltip: {
        ...getChartTooltipChrome(),
        borderRadius: 8,
        borderWidth: 1,
        shadow: true,
        useHTML: true,
        formatter: function () {
          const formattedValue = formatTooltipValue(this.y);
          const unit = parameterData?.unit || "";
          const textValue =
            this.point?.custom?.textValue
            ?? this.point?.options?.custom?.textValue;
          const textValueBlock = textValue
            ? `<p style="font-size: 14px; font-weight: 400; color: hsl(var(--muted-foreground)); margin-top: 6px;">${escapeTooltipHtml(textValue)}</p>`
            : "";
          return `
          <div style="padding: 12px;">
            <p style="font-size: 12px; color: hsl(var(--muted-foreground)); margin-bottom: 4px;">${formatChartTooltipDateTime(this.x)}</p>
            <p style="font-size: 18px; font-weight: 500; color: hsl(var(--foreground));">Value: ${formattedValue} ${unit}</p>
            ${textValueBlock}
          </div>
        `;
        },
        style: {
          color: "hsl(var(--foreground))",
        },
      },
      plotOptions: {
        line: {
          color: "hsl(188, 100%, 42%)",
          lineWidth: 2,
          marker: {
            enabled: false,
            states: {
              hover: {
                enabled: true,
                radius: 5,
                fillColor: "hsl(188, 100%, 42%)",
                lineColor: "hsl(var(--card))",
                lineWidth: 2,
              },
            },
          },
        },
      },
      series: [
        {
          name: parameterData?.parameter || "Value",
          data: trendData.map((item) => {
            const x = new Date(item.timestamp).getTime();
            const y = item.value;
            if (item.textValue) {
              return {
                x,
                y,
                custom: { textValue: item.textValue },
              };
            }
            return [x, y];
          }),
          color: "hsl(188, 100%, 42%)",
        },
      ],
      credits: {
        enabled: false,
      },
      legend: {
        enabled: false,
      },
    };
  }, [trendData, parameterData, theme]);

  const alarmLegendItems = useMemo(() => {
    const { legendItems } = buildAlarmAxisChrome(
      parameterData?.alarmSetPoints,
      [],
      theme === "dark"
    );
    return legendItems;
  }, [parameterData, theme]);

  if (!isOpen || !parameterData) return null;

  const displayValue = formatParameterValue(parameterData);
  const currentValueLabel =
    parameterData.status === "NoData"
      ? displayValue
      : typeof displayValue === "number"
        ? `${formatNumber(displayValue, 2)} ${parameterData.unit || ""}`.trim()
        : `${displayValue ?? "-"} ${parameterData.unit || ""}`.trim();

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center p-4'>
      {/* Overlay */}
      <div
        className='absolute inset-0 bg-black bg-opacity-50'
        onClick={onClose}
        data-testid='overlay-trend-popup'></div>

      {/* Modal Content */}
      <Card className='relative bg-card border border-border shadow-lg rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto'>
        {/* Header */}
        <CardHeader className='flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-card px-6 py-4 rounded-t-lg'>
          <div className='flex items-center gap-3'>
            <div className='p-2 bg-primary/10 rounded-lg'>
              <TrendingUp className='h-5 w-5 text-primary' />
            </div>
            <div>
              <CardTitle className='text-lg font-light text-foreground'>
                History Trend - {parameterData.parameter}
              </CardTitle>
              <p className='text-sm text-muted-foreground mt-1'>
                {assetNumber} • {location}
              </p>
            </div>
          </div>
          <Button
            variant='ghost'
            size='icon'
            onClick={onClose}
            className='h-8 w-8'
            data-testid='button-close-trend'>
            <X className='h-4 w-4' />
          </Button>
        </CardHeader>

        {/* Content */}
        <CardContent className='p-6'>
          {/* Time Period Selection */}
          <div className={cn(dateRangePillTrack, "mb-6")}>
            {timePeriods.map((period) => (
              <Button
                key={period.value}
                variant='outline'
                size='sm'
                onClick={() => setSelectedPeriod(period.value)}
                className={cn(
                  "rounded-full shadow-none font-medium",
                  selectedPeriod === period.value
                    ? dateRangePillSelected
                    : dateRangePillIdle
                )}
                data-testid={`button-period-${period.value}`}>
                {period.label}
              </Button>
            ))}
          </div>

          {/* Current Status Card */}
          <Card className='bg-card border border-border shadow-sm mb-6'>
            <CardContent className='p-4'>
              <div className='flex items-center justify-between gap-4'>
                <div className='min-w-0'>
                  <p className='text-sm text-muted-foreground mb-1'>
                    Current Value
                  </p>
                  <p className='text-2xl font-bold text-foreground'>
                    {currentValueLabel}
                  </p>
                </div>
                <ParameterStatusBadge param={parameterData} />
              </div>
            </CardContent>
          </Card>

          {/* Chart */}
          <Card className='bg-card border border-border shadow-sm'>
            <CardContent className='p-6'>
              <div className='h-96'>
                {isLoading ? (
                  <div className='flex items-center justify-center h-full'>
                    <div className='flex flex-col items-center gap-3'>
                      <Loader2 className='h-8 w-8 animate-spin text-primary' />
                      <p className='text-sm text-muted-foreground'>
                        Loading trend data...
                      </p>
                    </div>
                  </div>
                ) : error ? (
                  <div className='flex items-center justify-center h-full'>
                    <div className='text-center'>
                      <p className='text-red-600 mb-2'>{error}</p>
                      <Button
                        variant='outline'
                        size='sm'
                        onClick={fetchTrendData}
                        className='text-primary border-primary hover:bg-primary hover:text-primary-foreground'>
                        Try Again
                      </Button>
                    </div>
                  </div>
                ) : arrayUtils.isEmpty(trendData) ? (
                  <div className='flex items-center justify-center h-full'>
                    <p className='text-muted-foreground'>
                      No data available for the selected time period
                    </p>
                  </div>
                ) : (
                  <HighchartsReact
                    highcharts={Highcharts}
                    options={chartOptions}
                  />
                )}
              </div>

              {/* Setpoint Legend — coincident LoLo/Lo or Hi/HiHi collapse to one row */}
              {alarmLegendItems.length > 0 && (
                <div className='mt-4 pt-4 border-t border-border'>
                  <p className='text-sm font-medium text-foreground mb-2'>
                    Alarm Setpoints:
                  </p>
                  <div className='flex flex-wrap gap-4 text-xs'>
                    {alarmLegendItems.map((item) => (
                      <div
                        key={`${item.label}-${item.value}`}
                        className='flex items-center gap-2'>
                        <div
                          className='w-4 h-0.5'
                          style={{ borderTop: `2px solid ${item.color}` }}
                          aria-hidden
                        />
                        <span className='text-muted-foreground'>
                          {item.label}: {item.value} {parameterData.unit}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </CardContent>
      </Card>
    </div>
  );
}
